import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, MenuItem, TextField,
  Button, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Stack, Alert, Paper, LinearProgress, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, Tab, Tabs, InputAdornment
} from '@mui/material';
import {
  MenuBook, Add, Edit, Delete, CheckCircle, Schedule, Warning,
  Refresh, BarChart, People, FileDownload, Flag, Class,
  AccessTime, EventNote
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { settingsAPI } from '../services/api';
import { exportCSV } from '../utils/export';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('sms_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const STATUS_CFG = {
  planned:   { label: 'Planned',   color: 'primary', icon: <Schedule sx={{ fontSize:14 }} /> },
  covered:   { label: 'Covered',   color: 'success', icon: <CheckCircle sx={{ fontSize:14 }} /> },
  postponed: { label: 'Postponed', color: 'warning', icon: <Flag sx={{ fontSize:14 }} /> },
  cancelled: { label: 'Cancelled', color: 'error',   icon: <Delete sx={{ fontSize:14 }} /> },
};
const fmtD = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—';
const ptLabel = v => (v||'').replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase());

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2.5 }}>{children}</Box> : null;
}

// ── Lesson Entry Dialog ────────────────────────────────────────────────────────
function LessonDialog({ open, onClose, onSave, editing, teachers, classes, subjects, academicYears, defaultTeacherId, defaultClassId }) {
  const today = new Date().toISOString().split('T')[0];
  const BLANK = {
    teacher_id: defaultTeacherId || '', class_id: defaultClassId || '', subject_id: '',
    academic_year_id: '', term: '', week_number: '', lesson_number: 1,
    planned_date: today, topic: '', subtopic: '', objectives: '', activities: '',
    resources: '', assessment_method: '', homework_set: '', syllabus_ref: '',
    status: 'planned', actual_date: '', coverage_notes: ''
  };
  const [form, setForm] = useState(BLANK);
  const setF = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!open) return;
    if (editing) setForm({ ...BLANK, ...editing, planned_date: editing.planned_date?.split('T')[0]||today, actual_date: editing.actual_date?.split('T')[0]||'' });
    else {
      const cur = academicYears.find(y => y.is_current);
      setForm({ ...BLANK, academic_year_id: cur?.id||'', teacher_id: defaultTeacherId||'', class_id: defaultClassId||'' });
    }
  }, [open, editing]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor:'#0f2b4a', color:'white', display:'flex', alignItems:'center', gap:1 }}>
        <MenuBook /> {editing ? 'Edit Lesson Plan' : 'Add Lesson to Prep Book'}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ pt:1 }}>
          <Grid item xs={12}><Typography variant="caption" fontWeight={700} color="primary">ASSIGNMENT</Typography></Grid>
          {!defaultTeacherId && (
            <Grid item xs={6} sm={4}>
              <TextField select fullWidth size="small" label="Teacher" value={form.teacher_id} onChange={setF('teacher_id')}>
                {teachers.map(t => <MenuItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</MenuItem>)}
              </TextField>
            </Grid>
          )}
          <Grid item xs={6} sm={4}>
            <TextField select fullWidth size="small" label="Class *" value={form.class_id} onChange={setF('class_id')}>
              <MenuItem value="">Select class…</MenuItem>
              {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.stream?` (${c.stream})`:''}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField select fullWidth size="small" label="Subject *" value={form.subject_id} onChange={setF('subject_id')}>
              <MenuItem value="">Select subject…</MenuItem>
              {subjects.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField select fullWidth size="small" label="Academic Year *" value={form.academic_year_id} onChange={setF('academic_year_id')}>
              {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current?' ✓':''}</MenuItem>)}
            </TextField>
          </Grid>

          <Grid item xs={12}><Divider /><Typography variant="caption" fontWeight={700} color="primary" sx={{ mt:1, display:'block' }}>LESSON DETAILS</Typography></Grid>
          <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Term" value={form.term} onChange={setF('term')} placeholder="Term 1" /></Grid>
          <Grid item xs={6} sm={2}><TextField fullWidth size="small" type="number" label="Week #" value={form.week_number} onChange={setF('week_number')} /></Grid>
          <Grid item xs={6} sm={2}><TextField fullWidth size="small" type="number" label="Lesson #" value={form.lesson_number} onChange={setF('lesson_number')} /></Grid>
          <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="date" label="Planned Date *" value={form.planned_date} onChange={setF('planned_date')} InputLabelProps={{ shrink:true }} /></Grid>
          <Grid item xs={6} sm={2}><TextField fullWidth size="small" label="Syllabus Ref" value={form.syllabus_ref} onChange={setF('syllabus_ref')} placeholder="4.2.1" /></Grid>
          <Grid item xs={12}><TextField fullWidth size="small" label="Topic *" value={form.topic} onChange={setF('topic')} placeholder="Main topic of the lesson" /></Grid>
          <Grid item xs={12}><TextField fullWidth size="small" label="Subtopic" value={form.subtopic} onChange={setF('subtopic')} /></Grid>
          <Grid item xs={12}><TextField fullWidth size="small" label="Learning Objectives" value={form.objectives} onChange={setF('objectives')} multiline rows={2} placeholder="By the end of this lesson, students will be able to…" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Activities / Methods" value={form.activities} onChange={setF('activities')} multiline rows={2} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Resources / Materials" value={form.resources} onChange={setF('resources')} multiline rows={2} placeholder="Textbook p.45, worksheet 3…" /></Grid>
          <Grid item xs={6}><TextField fullWidth size="small" label="Assessment Method" value={form.assessment_method} onChange={setF('assessment_method')} placeholder="Q&A, written exercise, quiz…" /></Grid>
          <Grid item xs={6}><TextField fullWidth size="small" label="Homework Set" value={form.homework_set} onChange={setF('homework_set')} /></Grid>

          {editing && (
            <>
              <Grid item xs={12}><Divider /><Typography variant="caption" fontWeight={700} color="success.main" sx={{ mt:1, display:'block' }}>COVERAGE TRACKING</Typography></Grid>
              <Grid item xs={6} sm={3}>
                <TextField select fullWidth size="small" label="Status" value={form.status} onChange={setF('status')}>
                  {Object.entries(STATUS_CFG).map(([k,v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="date" label="Actual Date Taught" value={form.actual_date} onChange={setF('actual_date')} InputLabelProps={{ shrink:true }} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Coverage Notes" value={form.coverage_notes} onChange={setF('coverage_notes')} placeholder="What was actually covered, any deviations…" /></Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(form)} sx={{ bgcolor:'#0f2b4a' }}>{editing ? 'Update' : 'Add to Prep Book'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── My Prep Book (teacher's own view) ─────────────────────────────────────────
function MyPrepBook({ user, teachers, classes, subjects, academicYears }) {
  const { enqueueSnackbar } = useSnackbar();
  const [entries, setEntries]     = useState([]);
  const [progress, setProgress]   = useState(null);
  const [loading, setLoading]     = useState(false);
  const [selectedYear, setYear]   = useState('');
  const [filterClass, setClass]   = useState('');
  const [filterSubject, setSubj]  = useState('');
  const [filterStatus, setStatus] = useState('');
  const [dialog, setDialog]       = useState(false);
  const [editing, setEditing]     = useState(null);
  const [coveringId, setCovering] = useState(null);
  const [coverForm, setCoverForm] = useState({ actual_date: new Date().toISOString().split('T')[0], coverage_notes: '' });

  const myTeacherId = user?.teacher_id;

  const load = useCallback(async () => {
    if (!myTeacherId || !selectedYear) return;
    setLoading(true);
    try {
      const params = { teacher_id: myTeacherId, academic_year_id: selectedYear };
      if (filterClass)   params.class_id   = filterClass;
      if (filterSubject) params.subject_id = filterSubject;
      if (filterStatus)  params.status     = filterStatus;
      const [er, pr] = await Promise.all([
        api.get('/prep-book', { params }),
        api.get('/prep-book/progress', { params: { teacher_id: myTeacherId, academic_year_id: selectedYear, ...( filterClass && { class_id: filterClass }), ...(filterSubject && { subject_id: filterSubject }) } })
      ]);
      if (er.data.success) setEntries(er.data.data);
      if (pr.data.success) setProgress(pr.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant:'error' }); }
    finally { setLoading(false); }
  }, [myTeacherId, selectedYear, filterClass, filterSubject, filterStatus, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);

  const save = async (form) => {
    try {
      const payload = { ...form, teacher_id: myTeacherId };
      if (editing) { await api.put(`/prep-book/${editing.id}`, payload); enqueueSnackbar('Updated', { variant:'success' }); }
      else { await api.post('/prep-book', payload); enqueueSnackbar('Lesson added', { variant:'success' }); }
      setDialog(false); setEditing(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant:'error' }); }
  };

  const markCovered = async () => {
    try {
      await api.put(`/prep-book/${coveringId}/cover`, coverForm);
      enqueueSnackbar('Marked as covered', { variant:'success' });
      setCovering(null); load();
    } catch { enqueueSnackbar('Error', { variant:'error' }); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this lesson plan?')) return;
    try { await api.delete(`/prep-book/${id}`); enqueueSnackbar('Deleted', { variant:'success' }); load(); }
    catch { enqueueSnackbar('Error', { variant:'error' }); }
  };

  const s = progress?.summary;
  const coveragePct = s?.total > 0 ? Math.round((s.covered / s.total) * 100) : 0;
  const byWeek = progress?.by_week || [];

  // Group entries by week
  const grouped = entries.reduce((g, e) => {
    const k = e.week_number ? `Week ${e.week_number}` : 'Unscheduled';
    if (!g[k]) g[k] = [];
    g[k].push(e); return g;
  }, {});

  return (
    <Box>
      {/* Filters */}
      <Grid container spacing={2} sx={{ mb:2 }}>
        <Grid item xs={6} sm={3}>
          <TextField select fullWidth size="small" label="Academic Year" value={selectedYear} onChange={e => setYear(e.target.value)}>
            {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current?' ✓':''}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField select fullWidth size="small" label="Class" value={filterClass} onChange={e => setClass(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.stream?` (${c.stream})`:''}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField select fullWidth size="small" label="Subject" value={filterSubject} onChange={e => setSubj(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {subjects.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField select fullWidth size="small" label="Status" value={filterStatus} onChange={e => setStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(STATUS_CFG).map(([k,v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={3} sx={{ display:'flex', gap:1, justifyContent:'flex-end', alignItems:'center' }}>
          <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={() =>
            exportCSV('prep_book', ['Week','Lesson','Date','Class','Subject','Topic','Status','Actual Date'],
              entries.map(e => [e.week_number||'', e.lesson_number, fmtD(e.planned_date), `${e.class_name}${e.stream?` (${e.stream})`:''}`, e.subject_name, e.topic, e.status, fmtD(e.actual_date)]))
          }>Export</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setDialog(true); }} sx={{ bgcolor:'#0f2b4a', whiteSpace:'nowrap' }}>
            Add Lesson
          </Button>
        </Grid>
      </Grid>

      {/* Progress summary */}
      {s && (
        <Card variant="outlined" sx={{ borderRadius:2, mb:2 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb:0.5 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography fontSize={13} fontWeight={700}>Syllabus Coverage</Typography>
                    <Typography fontSize={13} fontWeight={800} color={coveragePct >= 75?'success.main':coveragePct>=50?'warning.main':'error.main'}>{coveragePct}%</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={Math.min(coveragePct,100)}
                    color={coveragePct>=75?'success':coveragePct>=50?'warning':'error'}
                    sx={{ height:10, borderRadius:5 }} />
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {[['Planned',s.planned,'primary'],['Covered',s.covered,'success'],['Overdue',s.overdue,'error'],['Postponed',s.postponed,'warning'],['Cancelled',s.cancelled,'default']].map(([l,v,c]) => (
                    <Chip key={l} label={`${v} ${l}`} size="small" color={c} sx={{ fontSize:10 }} />
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                {s.overdue > 0 && (
                  <Alert severity="warning" sx={{ fontSize:12 }}>
                    <strong>{s.overdue} overdue lesson(s)</strong> — planned dates have passed without being marked as covered.
                  </Alert>
                )}
                {coveragePct >= 90 && <Alert severity="success" sx={{ fontSize:12 }}>Excellent! You are on track with your syllabus coverage.</Alert>}
              </Grid>
            </Grid>

            {/* By-week progress */}
            {byWeek.length > 0 && (
              <Box sx={{ mt:1.5 }}>
                <Typography fontSize={11} fontWeight={700} color="text.secondary" gutterBottom>BY WEEK</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {byWeek.map(w => {
                    const wpct = w.total > 0 ? Math.round(w.covered/w.total*100) : 0;
                    const wkTooltip = 'Week ' + w.week_number + ': ' + w.covered + '/' + w.total + ' covered' + (w.overdue > 0 ? ' · ' + w.overdue + ' overdue' : '');
                    return (
                      <Tooltip key={w.week_number} title={wkTooltip}>
                        <Paper variant="outlined" sx={{ px:1, py:0.5, textAlign:'center', minWidth:36, cursor:'default',
                          bgcolor: wpct===100?'#e8f5e9': w.overdue>0?'#fff3f3':'white',
                          border: `1px solid ${wpct===100?'#a5d6a7':w.overdue>0?'#fca5a5':'#e0e0e0'}` }}>
                          <Typography fontSize={9} color="text.secondary">Wk{w.week_number}</Typography>
                          <Typography fontSize={12} fontWeight={700} color={wpct===100?'success.main':w.overdue>0?'error.main':'text.primary'}>{wpct}%</Typography>
                        </Paper>
                      </Tooltip>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Lesson entries by week */}
      {loading ? <CircularProgress /> : entries.length === 0 ? (
        <Paper variant="outlined" sx={{ p:5, textAlign:'center', borderRadius:2 }}>
          <MenuBook sx={{ fontSize:56, color:'text.disabled', mb:1 }} />
          <Typography color="text.secondary">No lessons planned yet. Click "Add Lesson" to start your prep book.</Typography>
        </Paper>
      ) : (
        Object.entries(grouped).sort((a,b) => {
          const na = parseInt(a[0].replace(/\D/g,'')) || 9999;
          const nb = parseInt(b[0].replace(/\D/g,'')) || 9999;
          return na - nb;
        }).map(([week, weekEntries]) => (
          <Card key={week} variant="outlined" sx={{ borderRadius:2, mb:1.5 }}>
            <CardContent sx={{ pb:'8px !important' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb:1 }}>
                <Typography fontWeight={700} fontSize={13}>{week}</Typography>
                <Stack direction="row" spacing={0.5}>
                  <Chip label={`${weekEntries.filter(e=>e.status==='covered').length}/${weekEntries.length} covered`} size="small"
                    color={weekEntries.every(e=>e.status==='covered')?'success':weekEntries.some(e=>e.status==='planned'&&new Date(e.planned_date)<new Date())?'error':'default'}
                    sx={{ fontSize:10 }} />
                </Stack>
              </Stack>
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow sx={{ bgcolor:'#f5f6fa' }}>
                    {['#','Date','Class','Subject','Topic','Syllabus','Status','Actions'].map(h => (
                      <TableCell key={h} sx={{ fontWeight:700, fontSize:11, py:0.8 }}>{h}</TableCell>
                    ))}
                  </TableRow></TableHead>
                  <TableBody>
                    {weekEntries.map((e,i) => {
                      const overdue = e.status==='planned' && new Date(e.planned_date) < new Date();
                      return (
                        <TableRow key={e.id} hover sx={{ bgcolor: overdue?'#fff3f3': e.status==='covered'?'#f1fdf4':'white' }}>
                          <TableCell sx={{ fontSize:11, fontWeight:700 }}>L{e.lesson_number}</TableCell>
                          <TableCell sx={{ fontSize:11, color: overdue?'error.main':'text.secondary', fontWeight: overdue?700:400 }}>
                            {fmtD(e.planned_date)}{overdue&&' ⚠'}
                          </TableCell>
                          <TableCell sx={{ fontSize:11 }}>{e.class_name}{e.stream?` (${e.stream})`:''}</TableCell>
                          <TableCell sx={{ fontSize:11 }}>{e.subject_name}</TableCell>
                          <TableCell>
                            <Typography fontSize={12} fontWeight={600}>{e.topic}</Typography>
                            {e.subtopic && <Typography fontSize={10} color="text.secondary">{e.subtopic}</Typography>}
                            {e.coverage_notes && e.status==='covered' && <Typography fontSize={10} color="success.main">✓ {e.coverage_notes}</Typography>}
                          </TableCell>
                          <TableCell sx={{ fontSize:11, fontFamily:'monospace', color:'text.secondary' }}>{e.syllabus_ref||'—'}</TableCell>
                          <TableCell>
                            <Chip label={STATUS_CFG[e.status]?.label} color={STATUS_CFG[e.status]?.color} size="small" sx={{ fontSize:10 }} />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.3}>
                              {e.status !== 'covered' && (
                                <Tooltip title="Mark as covered">
                                  <IconButton size="small" color="success" onClick={() => { setCovering(e.id); setCoverForm({ actual_date: new Date().toISOString().split('T')[0], coverage_notes:'' }); }}>
                                    <CheckCircle fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => { setEditing(e); setDialog(true); }}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton size="small" color="error" onClick={() => del(e.id)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        ))
      )}

      <LessonDialog open={dialog} onClose={() => { setDialog(false); setEditing(null); }} onSave={save} editing={editing}
        teachers={teachers} classes={classes} subjects={subjects} academicYears={academicYears}
        defaultTeacherId={myTeacherId} defaultClassId={filterClass} />

      {/* Mark covered dialog */}
      <Dialog open={!!coveringId} onClose={() => setCovering(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Mark as Covered</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt:1 }}>
            <Grid item xs={12}><TextField fullWidth size="small" type="date" label="Date Taught" value={coverForm.actual_date} onChange={e => setCoverForm(f => ({ ...f, actual_date: e.target.value }))} InputLabelProps={{ shrink:true }} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Coverage Notes (optional)" value={coverForm.coverage_notes} onChange={e => setCoverForm(f => ({ ...f, coverage_notes: e.target.value }))} multiline rows={2} placeholder="What was covered, any notes for next time…" /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCovering(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={markCovered}>Mark Covered</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Class Teacher Overview (sees all teachers' prep books for their class) ─────
function ClassTeacherOverview({ user, classes, academicYears }) {
  const { enqueueSnackbar } = useSnackbar();
  const [overview, setOverview]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [selectedYear, setYear]   = useState('');
  const [selectedClass, setClass] = useState('');
  const [myClasses, setMyClasses] = useState([]);

  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);

  useEffect(() => {
    // Find the class this teacher is a home teacher of
    if (user?.teacher_id && classes.length) {
      api.get('/settings/classes').then(r => {
        if (r.data.success) {
          const mine = r.data.data.filter(c => c.class_teacher_id == user.teacher_id);
          setMyClasses(mine);
          if (mine.length > 0 && !selectedClass) setClass(String(mine[0].id));
        }
      }).catch(() => {});
    }
  }, [user, classes]);

  const load = useCallback(async () => {
    if (!selectedClass || !selectedYear) return;
    setLoading(true);
    try {
      const r = await api.get('/prep-book/class-overview', { params: { class_id: selectedClass, academic_year_id: selectedYear } });
      if (r.data.success) setOverview(r.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant:'error' }); }
    finally { setLoading(false); }
  }, [selectedClass, selectedYear, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  // Group by teacher
  const byTeacher = overview.reduce((g, row) => {
    const key = `${row.teacher_id}`;
    if (!g[key]) g[key] = { first_name: row.first_name, last_name: row.last_name, employee_number: row.employee_number, subjects: [] };
    g[key].subjects.push(row);
    return g;
  }, {});

  return (
    <Box>
      <Alert severity="info" sx={{ mb:2, fontSize:12 }}>
        This view shows the prep book status of all teachers assigned to your home class. Use it to monitor syllabus coverage and identify any teachers who are falling behind.
      </Alert>
      <Stack direction="row" spacing={2} sx={{ mb:2 }} alignItems="center">
        <TextField select size="small" label="Academic Year" value={selectedYear} onChange={e => setYear(e.target.value)} sx={{ minWidth:200 }}>
          {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current?' ✓':''}</MenuItem>)}
        </TextField>
        {myClasses.length > 0 ? (
          <TextField select size="small" label="Your Class" value={selectedClass} onChange={e => setClass(e.target.value)} sx={{ minWidth:180 }}>
            {myClasses.map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.stream?` (${c.stream})`:''}</MenuItem>)}
          </TextField>
        ) : (
          <TextField select size="small" label="Class" value={selectedClass} onChange={e => setClass(e.target.value)} sx={{ minWidth:180 }}>
            <MenuItem value="">Select class…</MenuItem>
            {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.stream?` (${c.stream})`:''}</MenuItem>)}
          </TextField>
        )}
        <Tooltip title="Refresh"><IconButton size="small" onClick={load}><Refresh /></IconButton></Tooltip>
      </Stack>

      {loading ? <CircularProgress /> : !overview.length ? (
        <Paper variant="outlined" sx={{ p:5, textAlign:'center', borderRadius:2 }}>
          <Typography color="text.secondary">No class assignments found for this class. Ensure subjects have been assigned to teachers.</Typography>
        </Paper>
      ) : (
        Object.values(byTeacher).map(teacher => {
          const totalCovered = teacher.subjects.reduce((s,r) => s + (r.covered||0), 0);
          const totalPlanned = teacher.subjects.reduce((s,r) => s + (r.total_planned||0), 0);
          const totalOverdue = teacher.subjects.reduce((s,r) => s + (r.overdue||0), 0);
          const pct = totalPlanned > 0 ? Math.round(totalCovered/totalPlanned*100) : 0;

          return (
            <Card key={teacher.employee_number || teacher.first_name} variant="outlined" sx={{ borderRadius:2, mb:2, border: totalOverdue>0?'1px solid #fca5a5':undefined }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb:1.5 }}>
                  <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
                    <Paper variant="outlined" sx={{ width:40, height:40, display:'flex', alignItems:'center', justifyContent:'center', borderRadius:'50%', bgcolor:'#e8f0fe', fontWeight:800, fontSize:15, color:'#0f2b4a' }}>
                      {teacher.first_name[0]}{teacher.last_name[0]}
                    </Paper>
                    <Box>
                      <Typography fontWeight={700}>{teacher.first_name} {teacher.last_name}</Typography>
                      <Typography fontSize={11} color="text.secondary">{teacher.employee_number}</Typography>
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {totalOverdue > 0 && <Chip label={`${totalOverdue} overdue`} size="small" color="error" sx={{ fontWeight:700 }} />}
                    <Chip label={`${pct}% covered`} size="small" color={pct>=75?'success':pct>=50?'warning':'error'} sx={{ fontWeight:700 }} />
                  </Stack>
                </Stack>

                {totalPlanned > 0 && (
                  <Box sx={{ mb:1.5 }}>
                    <LinearProgress variant="determinate" value={Math.min(pct,100)}
                      color={pct>=75?'success':pct>=50?'warning':'error'}
                      sx={{ height:7, borderRadius:4 }} />
                  </Box>
                )}

                <Table size="small">
                  <TableHead><TableRow sx={{ bgcolor:'#f5f6fa' }}>
                    {['Subject','Planned','Covered','Pending','Overdue','Progress'].map(h => (
                      <TableCell key={h} sx={{ fontWeight:700, fontSize:11 }}>{h}</TableCell>
                    ))}
                  </TableRow></TableHead>
                  <TableBody>
                    {teacher.subjects.map((row, i) => {
                      const sp = row.total_planned > 0 ? Math.round((row.covered||0)/row.total_planned*100) : 0;
                      return (
                        <TableRow key={i} hover sx={{ bgcolor: row.overdue>0?'#fff8f0':'white' }}>
                          <TableCell sx={{ fontWeight:600, fontSize:12 }}>{row.subject_name}</TableCell>
                          <TableCell align="center">{row.total_planned||0}</TableCell>
                          <TableCell align="center"><Chip label={row.covered||0} size="small" color="success" sx={{ fontSize:10, fontWeight:700 }} /></TableCell>
                          <TableCell align="center">{row.pending||0}</TableCell>
                          <TableCell align="center">
                            {row.overdue > 0 ? <Chip label={row.overdue} size="small" color="error" sx={{ fontSize:10, fontWeight:700 }} /> : <Typography fontSize={12} color="text.secondary">—</Typography>}
                          </TableCell>
                          <TableCell sx={{ minWidth:80 }}>
                            {row.total_planned > 0 && <LinearProgress variant="determinate" value={Math.min(sp,100)} color={sp>=75?'success':sp>=50?'warning':'error'} sx={{ height:5, borderRadius:3 }} />}
                            <Typography fontSize={10} color="text.secondary">{sp}%</Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          );
        })
      )}
    </Box>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function PrepBook() {
  const { user }               = useAuth();
  const [tab, setTab]          = useState(0);
  const [academicYears, setAY] = useState([]);
  const [classes, setClasses]  = useState([]);
  const [subjects, setSubjects]= useState([]);
  const [teachers, setTeachers]= useState([]);

  const isClassTeacher = user?.role === 'class_teacher';

  useEffect(() => {
    settingsAPI.getAcademicYears().then(r => { if (r.data.success) setAY(r.data.data); }).catch(() => {});
    settingsAPI.getClasses().then(r => { if (r.data.success) setClasses(r.data.data); }).catch(() => {});
    api.get('/subjects').then(r => { if (r.data.success) setSubjects(r.data.data); }).catch(() => {});
    import('../services/api').then(({ teachersAPI }) => {
      teachersAPI.getAll({ limit:500 }).then(r => { if (r.data.success) setTeachers(r.data.data); }).catch(() => {});
    });
  }, []);

  return (
    <Box>
      <Box sx={{ mb:3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <MenuBook color="primary" /> Prep Book
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Plan lessons · Track syllabus coverage · Monitor progress{isClassTeacher ? ' · View your class teachers\' plans' : ''}
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_,v) => setTab(v)} sx={{ mb:1, borderBottom:1, borderColor:'divider' }}>
        <Tab icon={<MenuBook fontSize="small" />} label="My Prep Book" iconPosition="start" sx={{ fontSize:12 }} />
        {isClassTeacher && <Tab icon={<People fontSize="small" />} label="Class Overview" iconPosition="start" sx={{ fontSize:12 }} />}
      </Tabs>

      <TabPanel value={tab} index={0}>
        <MyPrepBook user={user} teachers={teachers} classes={classes} subjects={subjects} academicYears={academicYears} />
      </TabPanel>
      {isClassTeacher && (
        <TabPanel value={tab} index={1}>
          <ClassTeacherOverview user={user} classes={classes} academicYears={academicYears} />
        </TabPanel>
      )}
    </Box>
  );
}
